import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useDialogFocus } from '../../lib/dialogFocus';
import { X } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

/** The right slide-over panel — the reference tool's field-properties
 *  "side modal" (dialog-panel sidepanel slider-panel), re-themed. Same
 *  deliberate simplicity as Modal: Escape + backdrop close; a real focus
 *  trap arrives with the Radix dialog if nesting ever demands it. */
export function Sheet({
  open,
  onClose,
  title,
  children,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  // Step 140 — focus moves in on open, stays in while open (Tab wraps), and
  // goes back to the trigger on close; the page behind stops scrolling.
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogFocus(open, panelRef);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div
        className={cn(
          'flex h-full w-full flex-col border-l bg-card shadow-xl',
          'animate-in slide-in-from-right duration-200',
          wide ? 'max-w-xl' : 'max-w-md',
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <h2 id={titleId} className="min-w-0 truncate text-sm font-semibold">
            {title}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
